import React, { useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  PiQuestionFill,
  PiQuestion,
  PiSignOutFill,
  PiSignOut,
} from 'react-icons/pi'
import { LeftNavIconButton } from './LeftNavIconButton'
import { useLeftNavStore } from './left-nav-store'
import {
  isNavItemEnabled,
  hasSlideOut,
  getActiveNavSection,
  resolvePath,
} from './useNavItems'
import { useNavItems } from './NavItemsContext'
import { useAuth } from 'context/authentication/useAuth'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { Z_INDEX } from 'styles/z-index'
import type { NavItemConfig, NavSection } from './types'

export const LeftNavBar: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, isAllowed, user } = useAuth()
  const featureFlags = useFeatureFlagService()
  const { navItems } = useNavItems()

  const {
    isCollapsed,
    activeSection,
    isSlideOutOpen,
    setActiveSection,
    openSlideOut,
    closeSlideOut,
    lastVisited,
    setLastVisited,
  } = useLeftNavStore()

  // Filter nav items based on feature flags and user type
  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => {
      if (!isNavItemEnabled(item, featureFlags, user)) return false
      if (!isAllowed(item.minimumRole ?? 'Standard')) return false
      return true
    })
  }, [navItems, featureFlags, user, isAllowed])

  // Determine active section from current path
  const currentActiveSection = useMemo(
    (): NavSection | null => getActiveNavSection(location.pathname, navItems),
    [location.pathname, navItems]
  )

  /**
   * Navigation State Management (FIR-4955)
   *
   * Automatically tracks the user's last visited page within each navigation section.
   * This enables "memory" behavior where returning to a section restores the previous
   * page instead of resetting to the default.
   *
   * Example:
   * - User navigates to Targets > Commercial > Hotels
   * - User switches to Deals section
   * - User clicks Targets again → returns to Commercial > Hotels (not default Restaurants & Bars)
   *
   * State is persisted to localStorage via Zustand middleware for cross-session memory.
   */
  React.useEffect(() => {
    if (currentActiveSection) {
      setLastVisited(currentActiveSection, location.pathname)
    }
  }, [currentActiveSection, location.pathname, setLastVisited])

  /**
   * Handles navigation item clicks with intelligent routing (FIR-4955)
   *
   * Behavior:
   * 1. For sections WITH slide-out panels (Targets, Performance, Workspace, Settings):
   *    - First click: Opens panel AND navigates to last visited or default page
   *    - Subsequent clicks while open: Toggles panel closed
   *
   * 2. For sections WITHOUT slide-out panels (Deals, Campaigns, Samples, Rebates):
   *    - First click: Navigates to last visited page within this section
   *    - Second click (already active): Navigates back to the section's list page
   *    - Closes any open panels
   *
   * Navigation Priority:
   * - lastVisited[section] (user's previous page in this section)
   * - item.path (default page defined in NAV_ITEMS)
   */
  const handleNavClick = useCallback(
    (item: NavItemConfig) => {
      const isCurrentlyActive = currentActiveSection === item.id
      const itemHasSlideOut = hasSlideOut(item)

      if (itemHasSlideOut) {
        // For sections with slide-out panels
        if (isCurrentlyActive && isSlideOutOpen) {
          // Close slide-out if clicking the already active section with panel open
          closeSlideOut()
        } else {
          // Open slide-out AND navigate to last visited or default page
          openSlideOut(item.id)
          navigate(lastVisited[item.id] || resolvePath(item.path))
        }
      } else {
        // For sections without slide-out panels, navigate directly
        setActiveSection(item.id)
        closeSlideOut()
        // If already in this section, go back to the list page (item.path);
        // otherwise restore the last visited page within this section.
        if (isCurrentlyActive) {
          navigate(resolvePath(item.path))
        } else {
          navigate(lastVisited[item.id] || resolvePath(item.path))
        }
      }
    },
    [
      currentActiveSection,
      isSlideOutOpen,
      setActiveSection,
      openSlideOut,
      closeSlideOut,
      navigate,
      lastVisited,
    ]
  )

  const handleHelpClick = useCallback(() => {
    window.open('https://help.firstbite.io/', '_blank')
  }, [])

  const handleLogoutClick = useCallback(() => {
    logout()
  }, [logout])

  return (
    <nav
      className={`${isCollapsed ? 'hidden' : 'flex'} flex-col w-[var(--left-nav-width)] min-w-[var(--left-nav-width)] h-full bg-[var(--nav-bg-primary)] px-3 pb-6`}
      style={{ zIndex: Z_INDEX.LEFT_NAV, width: 'var(--left-nav-width)' }}
      aria-label="Main navigation"
      data-left-nav
    >
      <div className="flex flex-col flex-1 justify-between items-center min-h-0">
        {/* Top Section */}
        <div className="flex flex-col items-center gap-[20px] pt-1">
          {visibleNavItems.map((item) => (
            <LeftNavIconButton
              key={item.id}
              icon={item.icon}
              outlineIcon={item.outlineIcon}
              label={item.label}
              isSelected={currentActiveSection === item.id}
              isSecondaryActive={
                hasSlideOut(item) && activeSection === item.id && isSlideOutOpen
              }
              onClick={() => handleNavClick(item)}
              showLabel={true}
            />
          ))}
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col items-center gap-3">
          <LeftNavIconButton
            icon={<PiQuestionFill size={24} />}
            outlineIcon={<PiQuestion size={24} />}
            label="Help Center"
            onClick={handleHelpClick}
          />
          {/* Divider */}
          <div className="w-11 h-px bg-[var(--nav-text-primary)] opacity-50 my-2" />
          <LeftNavIconButton
            icon={<PiSignOutFill size={24} />}
            outlineIcon={<PiSignOut size={24} />}
            label="Log Out"
            onClick={handleLogoutClick}
          />
        </div>
      </div>
    </nav>
  )
}
